import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import GenericDialog from "../../dialog/generic-dialog/GenericDialog";
import { AiAgentConfigDialogContent, AiAgentConfigDialogFooter, AiAgentConfigDialogTitle } from "./components";
import styles from "./AiAgentConfigDialog.module.css";
import { ACTION_OPTION, AI_AGENT_CONFIG_TABS, DOCUMENT_OPTION } from "../../../constants";
import { shallowEqual } from "../../../utils";
import { useTrackEvent } from "../../../hooks";

const AiAgentConfigDialog = ({
	actionOptions = [],
	actionOptionsTitle = "",
	documentOptions = [],
	documentOptionsTitle = "",
	element = null,
	helperText = "",
	historyHeaderTitleKey = "",
	isTemplate = false,
	launchTitle = "",
	onClose,
	onGetHistory,
	onGetInformation,
	onLaunch,
	onRollbackAgentResults,
	onSave,
	open = false,
	promptHoverText = "",
	promptModelHelperText = "",
	promptModels = [],
	promptPlaceholder = "",
	promptTitle = "",
	quickLaunchEnabled = false,
	templateSelectEventMessage = "",
	title = "",
}) => {
	const projectId = useSelector(({ context }) => context?.project?.id);
	const [selectedTab, setSelectedTab] = useState(AI_AGENT_CONFIG_TABS.CONFIGURATION);
	const [stage, setStage] = useState(0);
	const [instructions, setInstructions] = useState("");
	const [documentOption, setDocumentOption] = useState(DOCUMENT_OPTION.AI);
	const [selectedDocuments, setSelectedDocuments] = useState({});
	const [actionOption, setActionOption] = useState(ACTION_OPTION.REPLACE);
	const [totalDocuments, setTotalDocuments] = useState(0);
	const [initState, setInitState] = useState(null);
	const [quickLaunchSelection, setQuickLaunchSelection] = useState([]);
	const trackEvent = useTrackEvent();
	const isStateSameAsInitial = useMemo(
		() =>
			initState?.instructions === instructions &&
			initState?.documentOption === documentOption &&
			initState?.actionOption === actionOption &&
			initState?.selectedDocuments?.documents?.is === selectedDocuments?.documents?.is &&
			shallowEqual(
				initState?.selectedDocuments?.documents?.elements || {},
				selectedDocuments?.documents?.elements || {}
			),
		[initState, actionOption, documentOption, instructions, selectedDocuments]
	);
	useEffect(() => {
		if (quickLaunchEnabled) {
			setSelectedTab(AI_AGENT_CONFIG_TABS.QUICK_LAUNCH);
		}
	}, [quickLaunchEnabled]);
	useEffect(() => {
		if ((element?.id || projectId) && typeof onGetInformation === "function" && open) {
			onGetInformation({ thematicId: element?.id, projectId })
				.then((data) => {
					if (data?.additionPrompt) {
						const responseActionOption = data?.integrationPolicy || ACTION_OPTION.REPLACE;
						const responseInstructions = data?.additionPrompt || "";
						let responseSelectedDocuments = {};
						let responseDocumentOptions = DOCUMENT_OPTION.AI;

						if (data?.documents) {
							responseSelectedDocuments = { documents: data?.documents };
						}
						if (data.selectDocumentAutomatically) {
							responseDocumentOptions = DOCUMENT_OPTION.AI;
						} else if (data.selectDocumentAutomatically === false) {
							responseDocumentOptions = DOCUMENT_OPTION.MANUAL;
						} else if (data.selectDocumentAutomatically === null) {
							responseDocumentOptions = DOCUMENT_OPTION.NONE;
						}
						if (responseInstructions) {
							setStage(1);
						}
						setInstructions(responseInstructions);
						setDocumentOption(responseDocumentOptions);
						setActionOption(responseActionOption);
						setSelectedDocuments(responseSelectedDocuments);
						setInitState({
							instructions: responseInstructions,
							documentOption: responseDocumentOptions,
							actionOption: responseActionOption,
							selectedDocuments: responseSelectedDocuments,
						});
						setTotalDocuments(data?.totalDocuments || 0);
					} else {
						setInstructions(promptModels[0]?.value);
					}
				})
				.catch(console.error);
		}
	}, [element, onGetInformation, projectId, promptModels, open]);
	const handleChangeSelectedTab = (_, newTab) => {
		setSelectedTab(newTab);
	};
	const handleChangeStage = (newStage) => {
		setStage(newStage);
	};
	const handleChangeInstructions = (newInstructions) => {
		if (templateSelectEventMessage?.length > 0) {
			trackEvent(templateSelectEventMessage);
		}
		setInstructions(newInstructions);
	};
	const handleChangeDocumentOption = (value) => {
		if (value === DOCUMENT_OPTION.AI) {
			setSelectedDocuments({});
		}
		setDocumentOption(value);
	};
	const handleClose = () => {
		setInstructions("");
		setDocumentOption(DOCUMENT_OPTION.AI);
		setActionOption(ACTION_OPTION.REPLACE);
		setSelectedDocuments([]);
		onClose();
	};
	const handleChangeDocuments = (newSelection) => {
		setSelectedDocuments(newSelection);
	};
	const handleChangeActionOption = (value) => {
		setActionOption(value);
	};
	const handleChangeQuickLaunchInstructions = (newInstructions) => {
		if (quickLaunchSelection.includes(newInstructions)) {
			setQuickLaunchSelection((prev) => prev.filter((item) => item !== newInstructions));
		} else {
			setQuickLaunchSelection((prev) => [...prev, newInstructions]);
		}
	};
	const handleSave = () => {
		if (typeof onSave === "function") {
			setInitState({
				instructions,
				documentOption,
				actionOption,
				selectedDocuments,
			});
			onSave(
				{ thematicId: element?.id || null },
				{
					integrationPolicy: actionOption,
					documents: documentOption === DOCUMENT_OPTION.AI ? {} : selectedDocuments.documents,
					additionPrompt: instructions,
					selectDocumentAutomatically:
						documentOption === DOCUMENT_OPTION.NONE ? null : documentOption === DOCUMENT_OPTION.AI,
				}
			);
		}
	};
	const handleLaunch = () => {
		if (typeof onLaunch === "function") {
			setInitState({
				instructions,
				documentOption,
				actionOption,
				selectedDocuments,
			});
			onLaunch(
				{ thematic: element, projectId },
				{
					integrationPolicy: actionOption,
					documents: documentOption === DOCUMENT_OPTION.AI ? {} : selectedDocuments.documents,
					additionPrompt: instructions,
					selectDocumentAutomatically:
						documentOption === DOCUMENT_OPTION.NONE ? null : documentOption === DOCUMENT_OPTION.AI,
				}
			);
		}
	};
	return (
		<GenericDialog disableBackdropClick className={styles.dialog} open={open} onClose={handleClose}>
			<AiAgentConfigDialogTitle helperText={helperText} title={title} onClose={handleClose} />
			<AiAgentConfigDialogContent
				actionOption={actionOption}
				actionOptions={actionOptions}
				actionOptionsTitle={actionOptionsTitle}
				documentOption={documentOption}
				documentOptions={documentOptions}
				documentOptionsTitle={documentOptionsTitle}
				element={element}
				historyHeaderTitleKey={historyHeaderTitleKey}
				instructions={instructions}
				isTemplate={isTemplate}
				promptHoverText={promptHoverText}
				promptModelHelperText={promptModelHelperText}
				promptModels={promptModels}
				promptPlaceholder={promptPlaceholder}
				promptTitle={promptTitle}
				quickLaunchEnabled={quickLaunchEnabled}
				quickLaunchSelection={quickLaunchSelection}
				selectedDocuments={selectedDocuments}
				selectedTab={selectedTab}
				stage={stage}
				totalDocuments={totalDocuments}
				onChangeActionOption={handleChangeActionOption}
				onChangeDocumentOption={handleChangeDocumentOption}
				onChangeDocuments={handleChangeDocuments}
				onChangeInstructions={handleChangeInstructions}
				onChangeQuickLaunchInstructions={handleChangeQuickLaunchInstructions}
				onChangeSelectedTab={handleChangeSelectedTab}
				onChangeStage={handleChangeStage}
				onGetHistory={onGetHistory}
				onRollbackAgentResults={onRollbackAgentResults}
			/>
			<AiAgentConfigDialogFooter
				disableLaunch={instructions?.length === 0}
				disableSave={instructions?.length === 0 || isStateSameAsInitial}
				displayLaunch={!!onLaunch}
				initState={initState}
				launchTitle={launchTitle}
				selectedTab={selectedTab}
				stage={stage}
				onChangeStage={handleChangeStage}
				onClose={onClose}
				onLaunch={handleLaunch}
				onSave={handleSave}
			/>
		</GenericDialog>
	);
};

export default AiAgentConfigDialog;
